/**
 * Per-module host detection for the Kusudama SPA.
 *
 * Origami's Caddy serves the same SPA bundle on three kinds of vhosts:
 *   - kusudama[-<env>].<domain> — canonical, URLs include /{module}
 *     (staging: kusudama-rec.aliadiss.com). On production this same canonical
 *     vhost is published as origami.aliadiss.com, which is what makes the
 *     admin module — it has no host of its own — reachable at /admin.
 *   - <module>-<env>.<domain>   — per-module, per-environment; URLs omit
 *     /{module} (staging: claimflow-rec.aliadiss.com, extranet-rec.aliadiss.com)
 *   - <module>.<domain>         — per-module without an environment suffix:
 *     the production form, URLs omit /{module} (claimflow.aliadiss.com,
 *     extranet.aliadiss.com)
 *
 * e.g. https://extranet-rec.aliadiss.com/eurosud boots as /extranet/eurosud,
 * the same route https://kusudama-rec.aliadiss.com/extranet/eurosud serves.
 *
 * We normalize the per-module variants by inserting the module segment
 * into window.location before React Router boots, so a single set of
 * route declarations matches every host.
 *
 * Everything else means "no module": single-label hosts (localhost), hosts
 * whose first label is not a name (IP literals), the canonical kusudama host
 * and the reserved labels below.
 */

/** Environment suffixes an origami vhost may carry: <module>-<env>.<domain>. */
const ENV_LABELS = ['rec', 'dev', 'preprod', 'prod'] as const;

/**
 * First DNS label of the host. Requires at least two labels (so `localhost`
 * never matches) and a leading letter (so `127.0.0.1` never matches).
 * Case-sensitive on purpose: window.location.hostname is always lowercase.
 */
const FIRST_LABEL_RE = /^([a-z][a-z0-9-]*)\./;

/** Trailing `-rec` / `-dev` / `-preprod` / `-prod` on that first label. */
const ENV_SUFFIX_RE = new RegExp(`-(?:${ENV_LABELS.join('|')})$`);

const CANONICAL_HOST_MODULE = 'kusudama';

/**
 * The production name of that same canonical, path-routed vhost. `origami` is
 * the back-end/product host, never a module: production serves the canonical
 * bundle at origami.aliadiss.com, so /admin there must stay /admin instead of
 * being rewritten to /origami/admin, which matches no route.
 */
const PRODUCTION_CANONICAL_HOST_LABEL = 'origami';

/**
 * Labels that are never a module: the canonical SPA host under either of its
 * names (with or without an environment suffix) and names infrastructure
 * vhosts legitimately use — a bare environment host (rec./dev./preprod./prod.)
 * or www.
 */
const RESERVED_MODULES: ReadonlySet<string> = new Set<string>([
  CANONICAL_HOST_MODULE,
  PRODUCTION_CANONICAL_HOST_LABEL,
  'www',
  ...ENV_LABELS,
]);

export function detectModuleFromHost(
  hostname: string = window.location.hostname,
): string | undefined {
  const label = FIRST_LABEL_RE.exec(hostname)?.[1];
  if (!label) return undefined;

  // The suffix is stripped only as a whole `-<env>` tail, so a module named
  // `foo-recipe` keeps its name instead of collapsing to `foo`.
  const mod = label.replace(ENV_SUFFIX_RE, '');
  if (RESERVED_MODULES.has(mod)) return undefined;
  return mod;
}

/**
 * If the current host is a per-module host, rewrites
 * window.location.pathname to include the /{module} prefix via
 * history.replaceState. Idempotent and safe to call once at boot.
 *
 * Returns the detected module, or undefined when no rewrite happened.
 */
export function normalizeModulePathFromHost(): string | undefined {
  const mod = detectModuleFromHost();
  if (!mod) return undefined;

  const prefix = `/${mod}`;
  const { pathname, search, hash } = window.location;
  if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return mod;

  const tail = pathname === '/' ? '' : pathname;
  window.history.replaceState(null, '', `${prefix}${tail}${search}${hash}`);
  return mod;
}
